/**
 * @link https://span.studio
 * @copyright Copyright (c) Span Studio LLC
 * @license https://github.com/Span-Studio/sta100-craft5/blob/main/LICENSE.md
 * 
 * Hosted Video.
 * UI for displaying iframe-based hosted videos on YouTube and Vimeo.
 * Some limitations on YouTube paths based on query vars btw. 
 * 
 * @author John Pobojewski <https://john.pobojewski.com
 */

class HostedVideo {
	init() {
		console.log("HostedVideo.js: init()");

		document.querySelectorAll("figure.video").forEach((figure) => {
			let embedded = false;
			const url = figure.getAttribute("data-src");
			let queries = figure.getAttribute("data-query") || "";

			if (!url) {
				return;
			}

			if (url.includes("vimeo.com")) {
				this.checkVimeo(figure, queries);
				embedded = true;
			} else if (url.includes("youtube.com") || url.includes("youtu.be")) {
				embedded = this.checkYouTube(figure, queries);
			}

			if (!embedded) {
				figure.classList.add("fit");
				const p = document.createElement("p");
				p.className = "centered";

				const a = document.createElement("a");
				a.className = "button";
				a.href = url;
				a.target = "_blank";
				a.textContent = "View external video";

				p.appendChild(a);
				figure.appendChild(p);
			}
		});
	}

	checkVimeo(figure, queries) {
		const url = figure.getAttribute("data-src");
		const apiUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`;

		// NOTE: This is synchronous in original, which is deprecated in fetch/XHR — so we use async logic.
		fetch(apiUrl)
			.then((response) => response.json())
			.then((data) => {
				if (data.video_id) {
					const id = data.video_id;
					figure.classList.add("widescreen");

					const iframe = document.createElement("iframe");
					iframe.src = `https://player.vimeo.com/video/${id}?api=1&player_id=${id}${queries}`;
					iframe.frameBorder = "0";
					iframe.setAttribute("portrait", "0");
					iframe.setAttribute("byline", "0");

					figure.appendChild(iframe);
				}
			})
			.catch((err) => {
				console.error("Vimeo fetch failed:", err);
			});
	}

	checkYouTube(figure, queries) {
		const url = figure.getAttribute("data-src");
		const id = this.getYouTubeId(url);
		if (!id) {
			return false;
		}

		figure.classList.add("widescreen");
		const queryString = this.normalizeQueryString(queries);
		const params = new URLSearchParams(queryString);
		const start = this.getYouTubeStartSeconds(url);
		if (start > 0 && !params.has("start")) {
			params.set("start", String(start));
		}

		const iframe = document.createElement("iframe");
		const serialized = params.toString();
		iframe.src = `https://youtube.com/embed/${id}${serialized ? `?${serialized}` : ""}`;
		iframe.frameBorder = "0";
		iframe.setAttribute("portrait", "0");
		iframe.setAttribute("byline", "0");
		iframe.setAttribute("allowfullscreen", "true");
		iframe.setAttribute(
			"allow",
			"accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
		);

		figure.appendChild(iframe);
		return true;
	}

	normalizeQueryString(queries) {
		return (queries || "").trim().replace(/^[?&]+/, "");
	}

	getYouTubeId(rawUrl) {
		const readPathSegment = (pathname, prefix) => {
			if (!pathname.startsWith(prefix)) {
				return "";
			}
			const segment = pathname.slice(prefix.length).split("/")[0];
			return segment || "";
		};

		try {
			const parsed = new URL(rawUrl);
			const host = parsed.hostname.replace(/^www\./, "");

			if (host === "youtu.be") {
				const segment = parsed.pathname.split("/").filter(Boolean)[0] || "";
				return this.isValidYouTubeId(segment) ? segment : "";
			}

			if (host.endsWith("youtube.com")) {
				const watchId = parsed.searchParams.get("v") || "";
				if (this.isValidYouTubeId(watchId)) {
					return watchId;
				}

				const embedId = readPathSegment(parsed.pathname, "/embed/");
				if (this.isValidYouTubeId(embedId)) {
					return embedId;
				}

				const shortsId = readPathSegment(parsed.pathname, "/shorts/");
				if (this.isValidYouTubeId(shortsId)) {
					return shortsId;
				}

				const liveId = readPathSegment(parsed.pathname, "/live/");
				if (this.isValidYouTubeId(liveId)) {
					return liveId;
				}

				const vId = readPathSegment(parsed.pathname, "/v/");
				if (this.isValidYouTubeId(vId)) {
					return vId;
				}

				if (parsed.pathname === "/attribution_link") {
					const nested = parsed.searchParams.get("u") || "";
					if (nested) {
						return this.getYouTubeId(`https://youtube.com${nested}`);
					}
				}
			}
		} catch {
			// Ignore parse error and continue to regex fallback.
		}

		const fallbackMatch = rawUrl.match(
			/(?:youtu\.be\/|youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/|live\/|v\/))([A-Za-z0-9_-]{11})/
		);
		return fallbackMatch ? fallbackMatch[1] : "";
	}

	getYouTubeStartSeconds(rawUrl) {
		const toSeconds = (value) => {
			if (!value) {
				return 0;
			}

			const numeric = Number(value);
			if (Number.isFinite(numeric)) {
				return Math.max(0, Math.floor(numeric));
			}

			const match = String(value).match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);
			if (!match) {
				return 0;
			}

			const hours = Number(match[1] || 0);
			const minutes = Number(match[2] || 0);
			const seconds = Number(match[3] || 0);
			return hours * 3600 + minutes * 60 + seconds;
		};

		try {
			const parsed = new URL(rawUrl);
			const t = parsed.searchParams.get("t") || parsed.searchParams.get("start");
			if (t) {
				return toSeconds(t);
			}

			const hash = parsed.hash.replace(/^#/, "");
			if (hash.startsWith("t=")) {
				return toSeconds(hash.slice(2));
			}
		} catch {
			// Ignore parse error and continue to regex fallback.
		}

		const queryT = rawUrl.match(/[?&](?:t|start)=([^&#]+)/i);
		if (queryT) {
			return toSeconds(decodeURIComponent(queryT[1]));
		}

		const hashT = rawUrl.match(/#t=([^&#]+)/i);
		if (hashT) {
			return toSeconds(decodeURIComponent(hashT[1]));
		}

		return 0;
	}

	isValidYouTubeId(id) {
		return /^[A-Za-z0-9_-]{11}$/.test(id);
	}
}

export default new HostedVideo();
